import memoize from 'lodash/memoize';
import { DateTime } from 'luxon';
import { insert, remove } from 'ramda';

import { localize } from 'common/utils';
import {
  LocalisedValue,
  OfferOverrides,
  SelectionCategory,
  SelectionOverridesResponse,
} from 'modules/shared-menu/types';

import { SharedMenuElement, SharedMenuOffer, SharedMenuOption, Offer } from './types';

const buildCategoryByOfferIdIndex = memoize((categories: SelectionCategory[]) => {
  const categoryByItemId: { [itemId: string]: SelectionCategory } = {};
  for (const category of flattenCategories(categories)) {
    for (const itemBinding of category.offer_bindings ?? []) {
      categoryByItemId[itemBinding.offer_id] = category;
    }
  }
  return categoryByItemId;
});
// Use WeakMap to avoid leaking memory. Overriding the cache of a memoized function instance is not documented, but it
// works with current Lodash memoize implementation. It is possible that in future this override breaks and memoize will
// resort to default cache implementation which leaks memory.
buildCategoryByOfferIdIndex.cache = new WeakMap();

export const getOfferCategory = (categories: SelectionCategory[], offerId: string): SelectionCategory | undefined => {
  const index = buildCategoryByOfferIdIndex(categories);
  return index[offerId];
};

export const createCategoryOption = (
  category: SelectionCategory,
  parentId: null | string,
  primaryLanguage: string,
) => ({
  value: category.id,
  label: localize(category.name, primaryLanguage),
  parentId,
  childrenIds: (category.subcategories ?? []).map(subcategory => subcategory.id),
});

export const isSharedMenuOffer = (el: SharedMenuElement): el is SharedMenuOffer => el.type === 'item';

export const isSharedMenuOption = (el: SharedMenuElement): el is SharedMenuOption => el.type === 'option';

export const checkIfItemIsAvailable = (enabledConfig: Offer['enabled']) => {
  const now = Date.now();

  const startIsAfterNow = !!(enabledConfig.after && DateTime.fromISO(enabledConfig.after).toMillis() > now);
  const endIsBeforeNow = !!(enabledConfig.before && DateTime.fromISO(enabledConfig.before).toMillis() <= now);
  if (startIsAfterNow || endIsBeforeNow) {
    return false;
  }

  return true;
};
export const flattenCategories = (categories: SelectionCategory[]) =>
  categories.flatMap(category => [category, ...(category.subcategories ?? [])]);

export const getParentCategory = (categoryId: string, categories: SelectionCategory[]) =>
  categories.find(category => category.subcategories?.some(subcategory => subcategory.id === categoryId)) ?? null;

/**
 * @param isoDate string in ISO format - local (browser) timezone
 * @returns string in ISO format - UTC timezone
 */
export const convertLocalIsoDateToUtcIsoDate = (isoDate: string): string => DateTime.fromISO(isoDate).toUTC().toISO();

export const applyOverridesToOffer = (offer: Offer, overrides: OfferOverrides): Offer => ({
  ...(offer ?? {}),
  ...(overrides ?? {}),
});

export const getOfferOverrides = (
  offer: Offer | undefined,
  selectionOverrides: SelectionOverridesResponse | undefined,
): OfferOverrides => {
  const overridesWithFieldsToReset = selectionOverrides?.offers[offer?.id ?? ''];

  if (!overridesWithFieldsToReset || Object.keys(overridesWithFieldsToReset).length === 0) {
    return {};
  }

  const { fields_to_reset: fieldsToReset, ...offerOverrides } = overridesWithFieldsToReset;

  const overridesPinnedAsNull = fieldsToReset?.reduce((acc, field) => ({ ...acc, [field]: null }), {});

  return {
    ...offerOverrides,
    ...overridesPinnedAsNull,
  };
};

export const updatePrimaryLanguageTranslation = (
  primaryLanguage: Language,
  newValue: string,
  translations: LocalisedValue[] = [],
): LocalisedValue[] => {
  const oldPrimaryLanguageTranslation = translations.find(translation => translation.lang === primaryLanguage);
  const newPrimaryLanguageTranslation = { lang: primaryLanguage, value: newValue, verified: true };

  if (!oldPrimaryLanguageTranslation) {
    return [newPrimaryLanguageTranslation, ...translations.map(translation => ({ ...translation, verified: false }))];
  }

  const primaryLanguageTranslationHasChanged = oldPrimaryLanguageTranslation.value !== newValue;

  return translations.map(translation =>
    translation.lang === primaryLanguage
      ? newPrimaryLanguageTranslation
      : { ...translation, verified: primaryLanguageTranslationHasChanged ? false : translation.verified },
  );
};

export const updateNonPrimaryLanguageTranslation = (
  language: Language,
  newValue: string,
  currentTranslations: LocalisedValue[] = [],
) => {
  const existingIndex = currentTranslations.findIndex(localisedValue => localisedValue.lang === language);

  const newTranslation: LocalisedValue = { lang: language, value: newValue, verified: true };

  const updatedAttrTranslations =
    existingIndex > -1
      ? insert(existingIndex, newTranslation, remove(existingIndex, 1, currentTranslations))
      : [...currentTranslations, newTranslation];

  return updatedAttrTranslations;
};
